import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { useConversas } from '@/conversas/ConversasProvider';
import { papelDoNumeroDoCanal } from '@/conversas/canais';
import { iniciarConversa } from '@/services/conversasService';
import { ehCelularBrasileiro, formatarTelefone, normalizarTelefone } from '@/lib/telefone';

/**
 * Abre uma conversa a partir de um telefone digitado.
 *
 * Não envia mensagem: o servidor cria (ou reabre) a conversa e devolve o id,
 * a tela abre a conversa, e o atendente escreve pelo campo de sempre. É o que
 * mantém uma porta só de saída de texto, com assinatura e variáveis.
 */

const MENSAGENS = {
  'functions/invalid-argument': 'Informe um celular com DDD, no formato +55 62 99999-9999.',
  // ★ A-8.1: "da Evolution" → "de Vendas".
  'functions/failed-precondition':
    'Este canal não permite iniciar conversa. Use o número de Vendas ou o da recepção.',
  'functions/permission-denied': 'Seu papel não atende este canal.',
  'functions/unauthenticated': 'Sua sessão expirou. Entre de novo.',
  'functions/unavailable': 'Não foi possível falar com o servidor. Tente de novo.',
};

/**
 * ★★★ WT lote 1b — O NÚMERO É CONFERIDO AQUI, ANTES DE IR AO SERVIDOR.
 *
 * ⚠⚠ Nasceu do **primeiro erro registrado em produção** (06/out 07:32, a Jéssica): uma
 * viagem até a Cloud Function para devolver *"Informe um celular com DDD"*. ★ A pessoa
 * descobria o erro **longe do campo onde o cometeu**, depois da espera — e cada viagem
 * dessas vira um registro de erro que **não é defeito de ninguém**.
 *
 * ★★ A validação é a MESMA do servidor (`ehCelularBrasileiro`, espelhada em
 * `lib/telefone.js` com teste que importa os dois lados). ⚠ **O servidor continua
 * validando** — a autoridade é dele; isto é conveniência, não substituição.
 */
export default function DialogoIniciarConversa({ aberto, aoFechar }) {
  const { clinicaId, canal, selecionar } = useConversas();
  const [telefone, setTelefone] = useState('');
  const [erro, setErro] = useState(null);
  const [abrindo, setAbrindo] = useState(false);

  // ★ O que de fato irá para o servidor, calculado a cada tecla.
  const e164 = normalizarTelefone(telefone);
  const valido = ehCelularBrasileiro(e164);
  // ⚠ Só mostra a prévia quando já há algo para mostrar: a pessoa digitando "6" não precisa
  // ver "+556" piscando, e o campo vazio não é erro — é o estado inicial.
  const digitou = telefone.trim().length >= 3;

  useEffect(() => {
    if (!aberto) return;
    setTelefone('');
    setErro(null);
  }, [aberto]);

  function fechar() {
    setErro(null);
    aoFechar();
  }

  async function abrir(evento) {
    evento.preventDefault();
    setErro(null);
    /**
     * ★★★ A RECUSA ACONTECE AQUI, SEM IR AO SERVIDOR.
     *
     * ⚠ `return` antes do `setAbrindo(true)`: o botão não deve piscar "Abrindo…" para um
     * número que nunca sairá da tela.
     */
    if (!valido) {
      setErro('Informe um CELULAR com DDD — ex.: (62) 99999-9999. Número fixo não recebe WhatsApp.');
      return;
    }
    setAbrindo(true);
    try {
      const { data } = await iniciarConversa({
        clinicaId,
        papel: papelDoNumeroDoCanal(canal),
        // ★ WT: já validado acima, e normalizado pela MESMA função do servidor.
        telefone: e164,
      });

      // A conversa nasce sempre no espaço do número do canal aberto, então ela
      // está na lista que está na tela — dá para selecionar direto.
      selecionar(data.conversaId);
      fechar();
    } catch (e) {
      console.error('[OdontoSDR] Falha ao iniciar conversa:', e);
      setErro(MENSAGENS[e.code] ?? e.message ?? 'Não foi possível abrir a conversa.');
    } finally {
      setAbrindo(false);
    }
  }

  return (
    <Dialogo titulo="Iniciar conversa" aberto={aberto} aoFechar={fechar} largura={420}>
      <form className="formulario" onSubmit={abrir}>
        <div className="campo">
          <label htmlFor="ic-telefone">Celular do contato</label>
          <input
            id="ic-telefone"
            type="tel"
            required
            autoFocus
            placeholder="+55 62 99999-9999"
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
          />
          <p className="campo-ajuda">
            Celular com DDD. A conversa abre vazia — a primeira mensagem você escreve nela.
          </p>
          {/* ★★★ WT — a PRÉVIA do que vai para o servidor, e o aviso na hora.
              ⚠ Sem isto a pessoa confirmava às cegas e descobria o erro depois da viagem. */}
          {digitou && valido && (
            <p className="campo-ajuda previa-telefone">
              Vai abrir para <strong>{formatarTelefone(e164)}</strong>
            </p>
          )}
          {digitou && !valido && (
            <p className="erro erro-campo" role="alert">
              Não parece um celular com DDD. Ex.: (62) 99999-9999.
            </p>
          )}
        </div>

        {erro && <p className="erro">{erro}</p>}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={fechar}>
            Cancelar
          </button>
          {/* ⚠ desabilitado enquanto o número não serve: o clique não tem para onde ir */}
          <button type="submit" className="btn-primario" disabled={abrindo || !valido}>
            {abrindo ? 'Abrindo…' : 'Abrir conversa'}
          </button>
        </footer>
      </form>
    </Dialogo>
  );
}
